import { roleService } from './roleService';
import { AppRole } from '../../types/auth';
import {
  FieldPermission,
  FieldPermissionMatrix,
  FieldAccessResult,
  PermissionCheckContext,
  FieldPermissionConfig,
} from '../../types/fieldPermissions';
import { offlineCache } from '../offlineCache';
import { PermissionsService } from '../permissionsService';

// Normalizes stage aliases used elsewhere in the app before hitting the API, which only serves
// the 4 canonical stages (DRAFT, SITE_ASSESSMENT, CLOSED, CONFIG) — PROCESSING is intentionally
// not seeded/servable server-side (see server/services/permissions.service.ts CANONICAL_STAGES).
const STAGE_ALIASES: Record<string, string> = {
  PROCESSING: 'DRAFT',
  // #1456: AWAITING_APPROVAL is Draft-like for field permissions until a dedicated matrix ships
  AWAITING_APPROVAL: 'DRAFT',
  CONFIGURATION: 'CONFIG',
  Initial: 'DRAFT',
};

function normalizeStage(stage: string): string {
  return STAGE_ALIASES[stage] ?? stage;
}

/** #1475: only opp/quote skip PC any-role elevation. Other `-` cells still elevate. */
const CHANNEL_OPP_QUOTE_FIELDS = new Set([
  'Opportunity Number',
  'Quote Number',
]);

function skipPartnerContributorElevationForHiddenChannelFields(
  field: string,
  fieldPermissions: Record<string, unknown>,
): boolean {
  if (!CHANNEL_OPP_QUOTE_FIELDS.has(field)) {
    return false;
  }
  const pcExplicitPermission = fieldPermissions['Partner Contributor'];
  return (
    pcExplicitPermission === '-' ||
    pcExplicitPermission === FieldPermission.HIDDEN
  );
}

class FieldPermissionService {
  private permissionsCache: FieldPermissionMatrix | null = null;
  private stagePermissionsCache: Map<string, FieldPermissionMatrix> = new Map();
  // Per-stage fetch timestamps for the TTL check. A single shared `lastFetched` (below) was
  // the original design but was never assigned after a load, so the cache-reuse branch in
  // loadStagePermissions was dead and every call re-fetched over HTTP (#1339 Bugs 2 & 5 —
  // footer buttons slow, Partner Contributor 1min+ load). Keyed by canonicalStage, matching
  // stagePermissionsCache and stageLoadPromises, so per-stage TTL is correct (loading one
  // stage never refreshes another's freshness window).
  private stageFetchedAt: Map<string, number> = new Map();
  private lastFetched: number = 0;
  private currentStage: string | null = null;
  private stageLoadPromises: Map<string, Promise<FieldPermissionMatrix>> =
    new Map();

  // Cache for computed field access results (for instant synchronous lookups)
  private fieldAccessCache: Map<string, FieldAccessResult> = new Map();

  private config: FieldPermissionConfig = {
    defaultPermission: FieldPermission.HIDDEN,
    enableCaching: true, // Re-enable caching for performance
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
  };

  /**
   * Load field permissions from JSON configuration
   * Uses DRAFT as fallback when no stage is provided
   */
  async loadPermissions(stage?: string): Promise<FieldPermissionMatrix> {
    // If no stage provided, use DRAFT as fallback
    if (!stage) {
      console.warn('No stage provided, falling back to DRAFT permissions');
      stage = 'DRAFT';
    }

    // Stage-specific permissions only
    return this.loadStagePermissions(stage);
  }

  /**
   * Load stage-specific field permissions
   */
  async loadStagePermissions(stage: string): Promise<FieldPermissionMatrix> {
    const now = Date.now();
    // Normalize BEFORE keying the in-memory cache/promise-dedup map — otherwise concurrent or
    // sequential calls with different aliases for the same canonical stage (e.g. 'PROCESSING'
    // and 'DRAFT', both real values passed by CreateRequest.tsx's import flow) don't dedupe and
    // each independently triggers its own API round-trip inside fetchPermissionsData.
    const canonicalStage = normalizeStage(stage);

    // If there's already a load promise for this stage, wait for it
    if (this.stageLoadPromises.has(canonicalStage)) {
      // console.log(`⏳ Waiting for existing load promise for stage: ${canonicalStage}`);
      return this.stageLoadPromises.get(canonicalStage)!;
    }

    // Return cached stage permissions if available and not expired
    if (
      this.config.enableCaching &&
      this.stagePermissionsCache.has(canonicalStage)
    ) {
      const cached = this.stagePermissionsCache.get(canonicalStage);
      const fetchedAt = this.stageFetchedAt.get(canonicalStage) ?? 0;
      if (cached && now - fetchedAt < this.config.cacheTimeout) {
        return cached;
      }
    }

    // Set current stage for race condition prevention
    this.currentStage = canonicalStage;
    // console.log(`🎯 Setting current stage to: ${canonicalStage}`);

    // Create and store the load promise
    const loadPromise = this.performStageLoad(canonicalStage);
    this.stageLoadPromises.set(canonicalStage, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      // Clean up the promise
      this.stageLoadPromises.delete(canonicalStage);
    }
  }

  private async performStageLoad(
    stage: string,
  ): Promise<FieldPermissionMatrix> {
    try {
      const permissionsData = await this.fetchPermissionsData(stage);

      if (this.config.enableCaching) {
        const replacesSeededMatrix = this.stagePermissionsCache.has(stage);
        this.stagePermissionsCache.set(stage, permissionsData);
        this.stageFetchedAt.set(stage, Date.now());
        if (replacesSeededMatrix) {
          // A matrix for this stage was already being consulted — either the bundled JSON
          // seeded by warmStagePermissionsCacheSync (#1469 optimistic first paint) or a
          // previous fetch past its TTL. Results computed from it are memoized in
          // fieldAccessCache, so drop this stage's entries and let sync consumers
          // recompute against the matrix the server just served (server stays
          // authoritative).
          this.clearFieldAccessCacheForStage(stage);
        }
      }

      // console.log(`✅ Stage permissions loaded successfully for stage: ${stage}`);
      return permissionsData;
    } catch (error) {
      console.error(`❌ Failed to load stage permissions for ${stage}:`, error);

      // No fallback to default permissions - return minimal fallback instead
      return this.getFallbackPermissions();
    }
  }

  /**
   * Fetch permissions data synchronously — used for fill-count warm cache.
   */
  private fetchPermissionsDataSync(stage: string): FieldPermissionMatrix {
    switch (stage) {
      case 'CLOSED':
        return require('../../config/fieldPermissions.CLOSED.json');
      case 'CONFIG':
      case 'CONFIGURATION':
        return require('../../config/fieldPermissions.CONFIG.json');
      case 'SITE_ASSESSMENT':
        return require('../../config/fieldPermissions.SITE_ASSESSMENT.json');
      case 'DRAFT':
      case 'Initial':
        return require('../../config/fieldPermissions.DRAFT.json');
      case 'PROCESSING':
        return require('../../config/fieldPermissions.DRAFT.json');
      default:
        throw new Error(
          `Unknown stage "${stage}" - no stage-specific permissions file available`,
        );
    }
  }

  /**
   * Fetch permissions data — API-first, falling back to offline cache, then to the bundled
   * JSON as a last resort.
   */
  private async fetchPermissionsData(
    stage: string,
  ): Promise<FieldPermissionMatrix> {
    const canonicalStage = normalizeStage(stage);

    try {
      const matrix = await PermissionsService.fetchFieldPermissionMatrix(
        canonicalStage,
      );
      await offlineCache.cacheFieldPermissionMatrix(canonicalStage, matrix);
      return matrix;
    } catch (apiError) {
      // API unreachable (network error), the endpoint errored (5xx / 404 for an unseeded
      // stage), or it returned a malformed 200 — all treated identically (no branching on
      // error type). Fall back to the offline cache, then to bundled JSON as the last resort.
      // Logged (not swallowed silently) so a systemic API outage stays visible even when a
      // fallback succeeds — otherwise nothing would ever surface it.
      console.warn(
        `Field permission API fetch failed for stage "${canonicalStage}", falling back:`,
        apiError,
      );
      const cached = await offlineCache.getCachedFieldPermissionMatrix(
        canonicalStage,
      );
      if (cached) {
        return cached;
      }

      // Bundled-JSON last resort — reuse the shared sync loader (development's
      // fetchPermissionsDataSync) rather than a second inline switch. It throws on an
      // unknown stage, which we rewrap below for a clearer message.
      try {
        return this.fetchPermissionsDataSync(canonicalStage);
      } catch (bundledError) {
        throw new Error(
          `Cannot load stage-specific permissions data for "${canonicalStage}": ${bundledError}`,
        );
      }
    }
  }

  /**
   * Drop memoized field-access results whose stage resolves to the given CANONICAL
   * stage. fieldAccessCache keys start with the RAW stage string of the check context
   * (which may be an alias like PROCESSING), so each key's stage segment is normalized
   * before comparing.
   */
  private clearFieldAccessCacheForStage(canonicalStage: string): void {
    const keysToDelete: string[] = [];
    this.fieldAccessCache.forEach((_, key) => {
      const rawStage = key.slice(0, key.indexOf(':'));
      if (normalizeStage(rawStage) === canonicalStage) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.fieldAccessCache.delete(key));
  }

  /**
   * Get fallback permissions when loading fails
   */
  private getFallbackPermissions(): FieldPermissionMatrix {
    console.warn('Using fallback permissions - all fields hidden');
    return {
      Default: {
        'Default Field': {
          'Supervisor NAM': FieldPermission.READ,
          'Supervisor EMEA': FieldPermission.READ,
          'Supervisor APAC': FieldPermission.READ,
        },
      },
    };
  }

  /**
   * Check field access for a user with multiple roles (synchronous version)
   * Uses cached permissions only - will return default access if permissions not loaded
   * Supports request-specific Partner Contributor access levels
   */
  checkFieldAccessSync(context: PermissionCheckContext): FieldAccessResult {
    let {
      userRoles,
      section,
      field,
      requestStage,
      partnerContributorAccessLevel,
      requestId,
      userEmail,
    } = context;

    // If partner contributor access level not provided, try to get from cache synchronously
    if (
      !partnerContributorAccessLevel &&
      requestId &&
      userEmail &&
      userRoles.includes(AppRole.PARTNER_CONTRIBUTOR)
    ) {
      try {
        // Synchronously import and check cache
        const teamInformationService =
          require('../teamInformationService').teamInformationService;
        const cacheKey = `${requestId}:${userEmail}`;

        if (teamInformationService.accessLevelCache?.has(cacheKey)) {
          partnerContributorAccessLevel =
            teamInformationService.accessLevelCache.get(cacheKey);
        }
      } catch (error) {
        console.error(
          'Failed to get partner contributor access level from cache:',
          error,
        );
      }
    }

    // Check computed field access cache first for instant lookups
    const cacheKey = this.getFieldAccessCacheKey({
      ...context,
      partnerContributorAccessLevel,
    });
    if (this.fieldAccessCache.has(cacheKey)) {
      return this.fieldAccessCache.get(cacheKey)!;
    }

    /**
     * NORMALIZE before reading, exactly as `loadStagePermissions` and `checkFieldAccess` do.
     *
     * `stagePermissionsCache` is written under the CANONICAL key — `performStageLoad` is
     * always called with `normalizeStage(stage)` — while this path used the raw request
     * stage. So a request at `CONFIGURATION` looked up a cache keyed `CONFIG` and missed
     * every single time, not intermittently, and fell through to `getDefaultAccess()`,
     * which is `isHidden: true`. Every field resolved through the synchronous path was
     * therefore hidden at Configuration for every role. Same for `PROCESSING` and
     * `Initial`, the other two aliases.
     *
     * Do not "simplify" this back to the raw value: the write key and the read key have to
     * agree, and canonical is the side that has three other callers.
     */
    const stage = normalizeStage(requestStage || 'DRAFT');
    const permissions = this.stagePermissionsCache.get(stage);

    if (!permissions) {
      // No cached permissions available, return default (hidden)
      // Note: Suppress warning to prevent console spam during async permission loading
      // console.warn(`No cached permissions for stage ${stage}, returning default access for ${section}.${field}`);
      return this.getDefaultAccess();
    }

    // Get the section permissions
    const sectionPermissions = permissions[section];
    if (!sectionPermissions) {
      return this.getDefaultAccess();
    }

    // Get the field permissions
    const fieldPermissions = sectionPermissions[field];
    if (!fieldPermissions) {
      return this.getDefaultAccess();
    }

    // Check permissions for each user role
    let canRead = false;
    let canWrite = false;
    let isHidden = true;

    for (const role of userRoles) {
      const roleDisplayName = roleService.getRoleDisplayName(role);
      const permission = fieldPermissions[roleDisplayName];

      // Special handling for Partner Contributor with request-specific access level
      if (
        roleDisplayName === 'Partner Contributor' &&
        partnerContributorAccessLevel
      ) {
        // Partner Contributors should NEVER have access to Delete button
        // Submit is allowed for collaboration, but Delete is restricted to internal roles only
        const restrictedButtons = ['Button - Delete'];
        if (section === 'Buttons' && restrictedButtons.includes(field)) {
          // Keep isHidden=true, canRead=false, canWrite=false
          continue; // Skip to next role
        }

        // The Submit button drives stage transitions — in CONFIGURATION it is the
        // "Close request" action. It MUST respect the EXPLICIT Partner Contributor
        // entry in the stage config, NOT the "any role can write" elevation used for
        // content fields below. The config grants Partner Contributor "W" for Submit
        // in SITE_ASSESSMENT (may advance) and "-" in CONFIGURATION (must not close),
        // so a read_write PC cannot close a request in CONFIGURATION (bug #529).
        if (section === 'Buttons' && field === 'Button - Submit') {
          const pcSubmitPermission = fieldPermissions['Partner Contributor'];
          const pcCanSubmit =
            partnerContributorAccessLevel === 'read_write' &&
            (pcSubmitPermission === 'W' ||
              pcSubmitPermission === FieldPermission.WRITE ||
              pcSubmitPermission === 'RW');
          if (pcCanSubmit) {
            canRead = true;
            canWrite = true;
            isHidden = false;
          }
          continue; // Explicit PC config entry is authoritative for the Submit button
        }

        // Partner Contributors should NEVER be able to modify team information fields
        // Even with read_write access, they can only view team information, not edit it
        const teamInformationFields = [
          'Vertiv Sponsors',
          'Vertiv Contributors',
          'Partner Contributors',
        ];
        if (
          section === 'Opportunity Information' &&
          teamInformationFields.includes(field)
        ) {
          // Allow read access only, never write access
          if (
            partnerContributorAccessLevel === 'read' ||
            partnerContributorAccessLevel === 'read_write'
          ) {
            canRead = true;
            isHidden = false;
            // canWrite stays false - explicitly prevent write access
          }
          continue; // Skip to next role
        }

        // #1475: explicit "-" on Partner Contributor is authoritative for
        // Opportunity/Quote only — do not elevate those via any-role logic.
        if (
          skipPartnerContributorElevationForHiddenChannelFields(
            field,
            fieldPermissions,
          )
        ) {
          continue;
        }

        // Partner Contributors with access levels get permissions based on what ANY role can do
        // This allows them to work on the entire form, not just Requestor-owned fields

        // Check if ANY role has write or read access to this field
        let anyRoleCanWrite = false;
        let anyRoleCanRead = false;

        for (const [roleName, rolePermission] of Object.entries(
          fieldPermissions,
        )) {
          const permStr = rolePermission as string;
          if (
            permStr === 'W' ||
            permStr === FieldPermission.WRITE ||
            permStr === 'RW'
          ) {
            anyRoleCanWrite = true;
            anyRoleCanRead = true;
            break; // Found write access, no need to check further
          } else if (permStr === 'R' || permStr === FieldPermission.READ) {
            anyRoleCanRead = true;
          }
        }

        if (partnerContributorAccessLevel === 'read') {
          // Read-only access: can see any field that ANY role can see, but cannot edit
          if (anyRoleCanRead || anyRoleCanWrite) {
            canRead = true;
            isHidden = false;
            // canWrite stays false
          }
        } else if (partnerContributorAccessLevel === 'read_write') {
          // Read/Write access: can edit ANY field that ANY role can edit
          if (anyRoleCanWrite) {
            canRead = true;
            canWrite = true;
            isHidden = false;
          } else if (anyRoleCanRead) {
            canRead = true;
            isHidden = false;
            // canWrite stays false for read-only fields
          }
        }

        continue; // Skip normal permission processing for Partner Contributor
      }

      if (permission) {
        const permStr = permission as string;
        switch (permStr) {
          case 'R':
          case FieldPermission.READ:
            canRead = true;
            isHidden = false;
            break;
          case 'W':
          case FieldPermission.WRITE:
            canRead = true;
            canWrite = true;
            isHidden = false;
            break;
          case 'RW':
            canRead = true;
            canWrite = true;
            isHidden = false;
            break;
          case '-':
          case FieldPermission.HIDDEN:
            // Keep current values (isHidden stays true, no access granted)
            break;
        }
      }
    }

    // A matrix WAS consulted to produce this, including when the answer is "hidden".
    // See `FieldAccessResult.resolved`.
    const result = {
      canRead,
      canWrite,
      isHidden,
      resolved: true,
    };

    // Cache the result for instant future lookups
    this.fieldAccessCache.set(cacheKey, result);

    return result;
  }

  /**
   * Generate cache key for field access results
   */
  private getFieldAccessCacheKey(context: PermissionCheckContext): string {
    const {
      userRoles,
      section,
      field,
      requestStage,
      partnerContributorAccessLevel,
      requestId,
    } = context;
    const rolesKey = userRoles.sort().join(',');
    const accessLevelKey = partnerContributorAccessLevel || 'none';
    const stageKey = requestStage || 'none';
    const reqIdKey = requestId || 'none';
    return `${stageKey}:${reqIdKey}:${rolesKey}:${accessLevelKey}:${section}:${field}`;
  }

  /**
   * Check field access for a user with multiple roles (async version)
   * Supports request-specific Partner Contributor access levels
   *
   * NOTE: Partner contributor access level should be pre-fetched and passed via context
   * to avoid async loading delays. Use teamInformationService.getPartnerContributorAccessLevel()
   * to fetch the access level before calling this method.
   */
  async checkFieldAccess(
    context: PermissionCheckContext,
  ): Promise<FieldAccessResult> {
    let {
      userRoles,
      section,
      field,
      requestStage,
      partnerContributorAccessLevel,
      requestId,
      userEmail,
    } = context;

    // If partner contributor access level not provided, try to fetch from service cache (synchronous)
    if (
      !partnerContributorAccessLevel &&
      requestId &&
      userEmail &&
      userRoles.includes(AppRole.PARTNER_CONTRIBUTOR)
    ) {
      try {
        // Try synchronous cache lookup first
        const { teamInformationService } = await import(
          '../teamInformationService'
        );
        const cacheKey = `${requestId}:${userEmail}`;

        // Check if it's in the cache (this is synchronous)
        if ((teamInformationService as any).accessLevelCache?.has(cacheKey)) {
          partnerContributorAccessLevel = (
            teamInformationService as any
          ).accessLevelCache.get(cacheKey);
        } else {
          // Fall back to async fetch (will cause loading delay)
          partnerContributorAccessLevel =
            await teamInformationService.getPartnerContributorAccessLevel(
              requestId,
              userEmail,
            );
        }
      } catch (error) {
        console.error(
          `❌ [Permission Service] Failed to get partner contributor access level for Request ${requestId}:`,
          error,
        );
      }
    }

    // Log the field access check with stage info
    // console.log(`🔍 checkFieldAccess: ${section}.${field} for stage: ${requestStage || 'undefined'}`);

    // If no stage is provided but we have a current stage, warn about potential mismatch
    if (!requestStage && this.currentStage) {
      console.warn(
        `⚠️  Field access check for ${section}.${field} with no stage, but current stage is ${this.currentStage}`,
      );
    }

    const permissions = await this.loadPermissions(requestStage);

    // Get the section permissions
    const sectionPermissions = permissions[section];
    if (!sectionPermissions) {
      return this.getDefaultAccess();
    }

    // Get the field permissions
    const fieldPermissions = sectionPermissions[field];
    if (!fieldPermissions) {
      return this.getDefaultAccess();
    }

    // Check permissions for each user role
    let canRead = false;
    let canWrite = false;
    let isHidden = true;

    for (const role of userRoles) {
      const roleDisplayName = roleService.getRoleDisplayName(role);
      const permission = fieldPermissions[roleDisplayName];

      // Special handling for Partner Contributor with request-specific access level
      if (
        roleDisplayName === 'Partner Contributor' &&
        partnerContributorAccessLevel
      ) {
        // Partner Contributors should NEVER have access to Delete button
        // Submit is allowed for collaboration, but Delete is restricted to internal roles only
        const restrictedButtons = ['Button - Delete'];
        if (section === 'Buttons' && restrictedButtons.includes(field)) {
          // Keep isHidden=true, canRead=false, canWrite=false
          continue; // Skip to next role
        }

        // The Submit button drives stage transitions — in CONFIGURATION it is the
        // "Close request" action. It MUST respect the EXPLICIT Partner Contributor
        // entry in the stage config, NOT the "any role can write" elevation used for
        // content fields below. The config grants Partner Contributor "W" for Submit
        // in SITE_ASSESSMENT (may advance) and "-" in CONFIGURATION (must not close),
        // so a read_write PC cannot close a request in CONFIGURATION (bug #529).
        if (section === 'Buttons' && field === 'Button - Submit') {
          const pcSubmitPermission = fieldPermissions['Partner Contributor'];
          const pcCanSubmit =
            partnerContributorAccessLevel === 'read_write' &&
            (pcSubmitPermission === 'W' ||
              pcSubmitPermission === FieldPermission.WRITE ||
              pcSubmitPermission === 'RW');
          if (pcCanSubmit) {
            canRead = true;
            canWrite = true;
            isHidden = false;
          }
          continue; // Explicit PC config entry is authoritative for the Submit button
        }

        // Partner Contributors should NEVER be able to modify team information fields
        // Even with read_write access, they can only view team information, not edit it
        const teamInformationFields = [
          'Vertiv Sponsors',
          'Vertiv Contributors',
          'Partner Contributors',
        ];
        if (
          section === 'Opportunity Information' &&
          teamInformationFields.includes(field)
        ) {
          // Allow read access only, never write access
          if (
            partnerContributorAccessLevel === 'read' ||
            partnerContributorAccessLevel === 'read_write'
          ) {
            canRead = true;
            isHidden = false;
            // canWrite stays false - explicitly prevent write access
          }
          continue; // Skip to next role
        }

        // #1475: explicit "-" on Partner Contributor is authoritative for
        // Opportunity/Quote only — do not elevate those via any-role logic.
        if (
          skipPartnerContributorElevationForHiddenChannelFields(
            field,
            fieldPermissions,
          )
        ) {
          continue;
        }

        // Partner Contributors with access levels get permissions based on what ANY role can do
        // This allows them to work on the entire form, not just Requestor-owned fields

        // Check if ANY role has write or read access to this field
        let anyRoleCanWrite = false;
        let anyRoleCanRead = false;

        for (const [roleName, rolePermission] of Object.entries(
          fieldPermissions,
        )) {
          const permStr = rolePermission as string;
          if (
            permStr === 'W' ||
            permStr === FieldPermission.WRITE ||
            permStr === 'RW'
          ) {
            anyRoleCanWrite = true;
            anyRoleCanRead = true;
            break; // Found write access, no need to check further
          } else if (permStr === 'R' || permStr === FieldPermission.READ) {
            anyRoleCanRead = true;
          }
        }

        if (partnerContributorAccessLevel === 'read') {
          // Read-only access: can see any field that ANY role can see, but cannot edit
          if (anyRoleCanRead || anyRoleCanWrite) {
            canRead = true;
            isHidden = false;
            // canWrite stays false
          }
        } else if (partnerContributorAccessLevel === 'read_write') {
          // Read/Write access: can edit ANY field that ANY role can edit
          if (anyRoleCanWrite) {
            canRead = true;
            canWrite = true;
            isHidden = false;
          } else if (anyRoleCanRead) {
            canRead = true;
            isHidden = false;
            // canWrite stays false for read-only fields
          }
        }

        continue; // Skip normal permission processing for Partner Contributor
      }

      if (permission) {
        const permStr = permission as string;
        switch (permStr) {
          case 'R':
          case FieldPermission.READ:
            canRead = true;
            isHidden = false;
            break;
          case 'W':
          case FieldPermission.WRITE:
            canRead = true;
            canWrite = true;
            isHidden = false;
            break;
          case 'RW':
            canRead = true;
            canWrite = true;
            isHidden = false;
            break;
          case '-':
          case FieldPermission.HIDDEN:
            // Keep current values (isHidden stays true, no access granted)
            break;
        }
      }
    }

    // A matrix WAS consulted to produce this, including when the answer is "hidden".
    // See `FieldAccessResult.resolved`.
    const result = {
      canRead,
      canWrite,
      isHidden,
      resolved: true,
    };

    // Cache the result for instant future lookups
    const cacheKey = this.getFieldAccessCacheKey(context);
    this.fieldAccessCache.set(cacheKey, result);

    return result;
  }

  /**
   * Check if user has specific field permission
   * Now requires a stage to be provided
   */
  async hasFieldPermission(
    userRoles: AppRole[],
    section: string,
    field: string,
    requiredPermission: FieldPermission,
    requestStage?: string,
  ): Promise<boolean> {
    const access = await this.checkFieldAccess({
      userRoles,
      section,
      field,
      requestStage,
    });

    switch (requiredPermission) {
      case FieldPermission.READ:
        return access.canRead;
      case FieldPermission.WRITE:
        return access.canWrite;
      case FieldPermission.HIDDEN:
        return access.isHidden;
      default:
        return false;
    }
  }

  /**
   * Get all sections that user has access to
   * Uses DRAFT as fallback when no stage is provided
   */
  async getAccessibleSections(
    userRoles: AppRole[],
    requestStage?: string,
  ): Promise<string[]> {
    // Use DRAFT as fallback if no stage provided
    const stage = requestStage || 'DRAFT';

    const permissions = await this.loadPermissions(stage);
    const accessibleSections: string[] = [];

    for (const sectionName of Object.keys(permissions)) {
      const hasAccess = await this.hasSectionAccess(
        userRoles,
        sectionName,
        stage,
      );
      if (hasAccess) {
        accessibleSections.push(sectionName);
      }
    }

    return accessibleSections;
  }

  /**
   * Check if user has access to any field in a section
   * Uses DRAFT as fallback when no stage is provided
   */
  async hasSectionAccess(
    userRoles: AppRole[],
    section: string,
    requestStage?: string,
  ): Promise<boolean> {
    // Use DRAFT as fallback if no stage provided
    const stage = requestStage || 'DRAFT';

    const permissions = await this.loadPermissions(stage);
    const sectionPermissions = permissions[section];

    if (!sectionPermissions) {
      return false;
    }

    // Check if user has access to at least one field in the section
    for (const fieldName of Object.keys(sectionPermissions)) {
      const access = await this.checkFieldAccess({
        userRoles,
        section,
        field: fieldName,
        requestStage: stage,
      });

      if (!access.isHidden) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get all fields in a section that user can access
   * Uses DRAFT as fallback when no stage is provided
   */
  async getAccessibleFields(
    userRoles: AppRole[],
    section: string,
    requestStage?: string,
    requestId?: string,
    userEmail?: string,
  ): Promise<{ fieldName: string; access: FieldAccessResult }[]> {
    // Use DRAFT as fallback if no stage provided
    const stage = requestStage || 'DRAFT';

    const permissions = await this.loadPermissions(stage);
    const sectionPermissions = permissions[section];

    if (!sectionPermissions) {
      return [];
    }

    const accessibleFields: { fieldName: string; access: FieldAccessResult }[] =
      [];

    for (const fieldName of Object.keys(sectionPermissions)) {
      const access = await this.checkFieldAccess({
        userRoles,
        section,
        field: fieldName,
        requestStage: stage,
        requestId,
        userEmail,
      });

      if (!access.isHidden) {
        accessibleFields.push({
          fieldName,
          access,
        });
      }
    }

    return accessibleFields;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<FieldPermissionConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Clear cache if caching is disabled
    if (!this.config.enableCaching) {
      this.clearCache();
    }
  }

  /**
   * Clear permissions cache - optionally clear only a specific stage
   */
  clearCache(stage?: string): void {
    if (stage) {
      // stagePermissionsCache/stageLoadPromises are keyed by canonicalStage (see
      // loadStagePermissions) — normalize here too, or clearCache('PROCESSING') would silently
      // miss an entry that was actually stored under 'DRAFT'. fieldAccessCache stays keyed by
      // the raw stage string (unchanged, pre-existing behavior — checkFieldAccess never
      // normalizes its own cache key), so its deletion below intentionally still uses `stage`.
      const canonicalStage = normalizeStage(stage);
      this.stagePermissionsCache.delete(canonicalStage);
      this.stageFetchedAt.delete(canonicalStage);
      this.stageLoadPromises.delete(canonicalStage);

      // Clear field access cache entries for this stage
      const keysToDelete: string[] = [];
      this.fieldAccessCache.forEach((_, key) => {
        if (key.startsWith(`${stage}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.fieldAccessCache.delete(key));
    } else {
      // Clear all caches
      this.permissionsCache = null;
      this.stagePermissionsCache.clear();
      this.stageFetchedAt.clear();
      this.stageLoadPromises.clear();
      this.fieldAccessCache.clear(); // Clear computed field access cache
      this.currentStage = null;
      this.lastFetched = 0;
    }
  }

  /**
   * Get default access when no permissions are found
   */
  /**
   * The answer when no matrix could be consulted.
   *
   * `resolved: false` is the load-bearing part. The values stay hidden/deny so a caller that
   * ignores the flag is no less safe than before, but a caller that CAN retry — see
   * `SimplePermissionWrapper` — must be able to tell this apart from a matrix that genuinely
   * resolved the field to hidden, or it renders a denial nobody established.
   */
  private getDefaultAccess(): FieldAccessResult {
    return {
      canRead: false,
      canWrite: false,
      isHidden: true,
      resolved: false,
    };
  }

  /**
   * Synchronously warm stage permissions for fill-count lookups (Create Request basic view).
   */
  warmStagePermissionsCacheSync(stage?: string | null): void {
    // Share normalizeStage with every other cache reader/writer so the seed lands under
    // the same key checkFieldAccessSync reads (a raw 'PROCESSING' used to warm a
    // 'PROCESSING' entry that the canonical 'DRAFT' lookup could never see). 'New' is an
    // extra alias only this entry point receives.
    let normalized = normalizeStage(stage ?? 'DRAFT');
    if (normalized === 'New') {
      normalized = 'DRAFT';
    }

    if (this.stagePermissionsCache.has(normalized)) {
      return;
    }

    try {
      const permissionsData = this.fetchPermissionsDataSync(normalized);
      if (this.config.enableCaching) {
        this.stagePermissionsCache.set(normalized, permissionsData);
      }
    } catch {
      // Fall back to hidden defaults when stage file cannot load synchronously.
    }
  }

  /**
   * Validate permissions matrix structure for a specific stage
   * Uses DRAFT as fallback when no stage is provided
   */
  async validatePermissions(
    requestStage?: string,
  ): Promise<{ isValid: boolean; errors: string[] }> {
    const errors: string[] = [];

    // Use DRAFT as fallback if no stage provided
    const stage = requestStage || 'DRAFT';

    try {
      const permissions = await this.loadPermissions(stage);

      // Check if permissions object exists
      if (!permissions || typeof permissions !== 'object') {
        errors.push('Permissions matrix is not a valid object');
        return { isValid: false, errors };
      }

      // Check structure
      for (const [sectionName, section] of Object.entries(permissions)) {
        if (!section || typeof section !== 'object') {
          errors.push(`Invalid section: ${sectionName}`);
          continue;
        }

        for (const [fieldName, field] of Object.entries(section)) {
          if (!field || typeof field !== 'object') {
            errors.push(`Invalid field: ${sectionName}.${fieldName}`);
            continue;
          }

          // Validate permission values
          for (const [roleName, permission] of Object.entries(field)) {
            const validPermissions = [
              'R',
              'W',
              'RW',
              '-',
              ...Object.values(FieldPermission),
            ];
            if (!validPermissions.includes(permission as string)) {
              errors.push(
                `Invalid permission value "${permission}" for ${sectionName}.${fieldName}.${roleName}`,
              );
            }
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    } catch (error) {
      errors.push(`Validation error: ${error}`);
      return { isValid: false, errors };
    }
  }
}

// Export singleton instance
export const fieldPermissionService = new FieldPermissionService();
